/* Copyright (c) 2022-2026, NVIDIA CORPORATION.  All rights reserved.
 *
 * NVIDIA CORPORATION and its licensors retain all intellectual property
 * and proprietary rights in and to this software, related documentation
 * and any modifications thereto.  Any use, reproduction, disclosure or
 * distribution of this software and related documentation without an express
 * license agreement from NVIDIA CORPORATION is strictly prohibited.
 */

import { Injectable, NgZone } from '@angular/core';
import { Observable, Subscriber } from 'rxjs';
import { FullBridgeCommand } from '../bridge/bridge.service';
import { LoggingService } from '../logging/logging.service';

declare const Android: { dispatch: Function; unregisterHandler: Function };

interface Handler {
    observer: Subscriber<any>;
    persistent: boolean;
}

export interface AndroidNativeResult {
    response?: any;
    errorCode?: number;
    errorMessage?: string;
}

interface HandlerQueryResult {
    handlerId: number;
    args: string;
}

interface UnregisterHandlerQuery {
    handlerId: number;
}

@Injectable({
    providedIn: 'root'
})
export class AndroidHelperService {
    private static IdGenerator = class {
        private static count = 0;

        public static next() {
            return ++this.count;
        }
    };

    private static handlerMap: { [key: number]: Handler } = {};

    constructor(
        loggingService: LoggingService,
        private ngZone: NgZone
    ) {
        Android.dispatch = this.dispatch.bind(this);
        Android.unregisterHandler = this.unregisterHandler.bind(this);
    }

    private dispatch(query: HandlerQueryResult): void {
        if (!AndroidHelperService.handlerMap[query.handlerId]) {
            throw new Error(`Cannot find handler Id=${query.handlerId}.`);
        }
        const handler = AndroidHelperService.handlerMap[query.handlerId];
        this.parseResponse(handler.observer, query.args);

        // Automatically dispose the non-persistent queries
        if (!handler.persistent) {
            delete AndroidHelperService.handlerMap[query.handlerId];
        }
    }

    private parseResponse<ResponseType>(observer: Subscriber<AndroidNativeResult>, queryResultString: string) {
        const queryResult: object = JSON.parse(queryResultString);
        const successful: boolean = queryResult['successful'];
        const errorCode: number = queryResult['errorCode'];
        const errorMessage: string = queryResult['errorMessage'];
        const response: any = queryResult['response'];

        if (successful) {
            const result: AndroidNativeResult = {
                response
            };
            if (NgZone.isInAngularZone()) {
                observer.next(result);
            } else {
                this.ngZone.run(() => {
                    observer.next(result);
                });
            }
        } else {
            const result: AndroidNativeResult = {
                errorCode,
                errorMessage
            };
            if (NgZone.isInAngularZone()) {
                observer.error(result);
            } else {
                this.ngZone.run(() => {
                    observer.error(result);
                });
            }
        }
    }

    private unregisterHandler(query: UnregisterHandlerQuery): void {
        if (!AndroidHelperService.handlerMap[query.handlerId]) {
            throw new Error(`Cannot find handler Id=${query.handlerId}.`);
        }
        AndroidHelperService.handlerMap[query.handlerId].observer.complete();
        delete AndroidHelperService.handlerMap[query.handlerId];
    }

    public doesCommandExist(module: String, method: String): boolean {
        return Android['hasCommand'] && Android['hasCommand'](module, method);
    }

    public executeCommandNative<ParamType extends object>(
        fullBridgeCommand: FullBridgeCommand<ParamType>,
        persistent: boolean,
        shouldLogPayload: boolean
    ): Observable<AndroidNativeResult> {
        const request: string = JSON.stringify(fullBridgeCommand);
        const queryData = {
            request,
            persistent,
            handlerId: AndroidHelperService.IdGenerator.next()
        };

        const queryDataString: string = JSON.stringify(queryData);

        const androidNativeObservable = new Observable<AndroidNativeResult>(observer => {
            AndroidHelperService.handlerMap[queryData.handlerId] = {
                observer,
                persistent
            };
        });

        // The response will be sent to the dispatch function and find out the observer by handler Id from query data.
        Android['executeCommand'](queryDataString);

        return androidNativeObservable;
    }
}
